import React from 'react';
import { Section } from '@/pages/LandingComplete';
import SectionHeader from './SectionHeader';
import { CheckCircle, Laptop2, Users } from 'lucide-react';

interface ServiceCard {
  badge?: string;
  badgeColor?: string;
  title: string;
  description: string;
  items: string[];
  image?: string;
  imageAlt?: string;
}

interface ServicesContent {
  title?: string;
  titleHighlight?: string;
  subtitle?: string;
  cards?: ServiceCard[];
}

export function DynamicServices({ content }: { content: ServicesContent }) {
  const cards = content.cards || [];
  const gradients = [
    'from-blue-50 to-indigo-50',
    'from-green-50 to-emerald-50',
  ];
  const badgeColors = [
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
  ];
  const icons = [Laptop2, Users];

  return (
    <Section id="leistungen">
      <SectionHeader 
        number="02" 
        title={content.title || "Unsere"} 
        highlight={content.titleHighlight || "Leistungen"}
        subtitle={content.subtitle || "Maßgeschneiderte IT-Unterstützung für Ihren Bedarf"}
      />

      <div className="grid items-start gap-16 lg:grid-cols-2">
        {cards.map((card, i) => {
          const Icon = icons[i % icons.length];
          return (
            <div key={card.title + i} className="group">
              <div className={`bg-gradient-to-br ${gradients[i % gradients.length]} rounded-3xl p-8 transition-all duration-500 hover:shadow-xl`}>
                <div className="mb-6">
                  <div className={`inline-flex items-center gap-2 rounded-full ${badgeColors[i % badgeColors.length]} px-4 py-2 text-sm font-semibold mb-4`}>
                    <Icon size={16} />
                    {card.badge}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{card.title}</h3>
                  <p className="text-slate-600 leading-relaxed mb-6">{card.description}</p>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle size={20} className="mt-0.5 shrink-0 text-[--brand-primary]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                {card.image && (
                  <div className="relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-xl transition-shadow">
                    <img 
                      src={card.image} 
                      alt={card.imageAlt || card.title} 
                      className="h-64 w-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}
